import { AfterViewInit, Component, OnInit, ViewChild, inject } from '@angular/core';
import { MatMenuPanel } from '@angular/material/menu';
import { MatDrawerMode } from '@angular/material/sidenav';
import { NavigationEnd, NavigationError, Router, RouterOutlet } from '@angular/router';
import { IpmAuthService } from '@core/services/ipm-auth.service';
import { StatusBar } from '@capacitor/status-bar';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { SnackBarService } from 'osc-reference-app-common';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { LoggerService } from '@shared/services/logger.service';
import { BluetoothResponse } from '@core/interfaces/bluetooth-response';
import { AppIdentity, AuthService, Guid, PlatformComponent, PlatformService } from '@evc/platform';
import { BtnActionEnum, MenuItemType } from '@evc/web-components';
import { OscSpinnerComponent } from '@osc-utils/components/osc-spinner/osc-spinner.component';
import { ConfigurationService } from '@osc-utils/services/configuration.service';
import { SpinnerService } from '@osc-utils/services/spinner.service';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { FeatureToggleService } from '@core/services/feature-toggle.service';
import { CurrentUserService } from '@core/services/current-user.service';
import { FeatureToggleCode } from '@core/enums/feature-toggle-code';
import { firstValueFrom, Observable } from 'rxjs';
import { PersistedOrganizationService } from '@core/services/persisted-organization.service';
import { APP_VERSION } from '@shared/tokens/app-version.token';
import { BluetoothService } from '@core/services/bluetooth.service';
import { TenantRegistrationService } from './web/tenant-registration/services/tenant-registration.service';
import { RoutingService } from './core/services/routing.service';
import { SplashScreenComponent } from './osc-reference-app-common/components/splash-screen/splash-screen.component';
import { SyncBackupService } from './mobile/services/sync-backup.service';
import { AutoSyncService } from './mobile/services/auto-sync.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [SplashScreenComponent, OscSpinnerComponent, RouterOutlet, TranslateModule, PlatformComponent],
})
export class AppComponent implements OnInit, AfterViewInit {
  @ViewChild('appSpinner') public appSpinner: OscSpinnerComponent;
  @ViewChild('languages') public languageMenu: MatMenuPanel;
  @ViewChild('settings') public settingMenu: MatMenuPanel;
  public configurationService = inject(ConfigurationService);
  public spinnerService = inject(SpinnerService);
  public opened = false;
  public openedOnInit = false;
  public sidenavMode: MatDrawerMode;
  public isMobile = this.configurationService.isMobileApp();
  public appIdentification: AppIdentity;
  public settingsMenu: MenuItemType[];

  #logger = inject(LoggerService);
  #authService = inject(IpmAuthService);
  #router = inject(Router);
  #snackBarService = inject(SnackBarService);
  readonly #appVersion = inject(APP_VERSION);
  #bluetoothService = inject(BluetoothService);
  #routingService = inject(RoutingService);
  #translate = inject(TranslateService);
  #media = inject(BreakpointObserver);
  #platformService = inject(PlatformService);
  #evcAuthService = inject(AuthService);
  #featureToggleService = inject(FeatureToggleService);
  #syncBackupService = inject(SyncBackupService);
  #currentUserService = inject(CurrentUserService);
  #autoSyncService = inject(AutoSyncService);
  #persistedOrganizationService = inject(PersistedOrganizationService);
  readonly #tenantRegistrationService = inject(TenantRegistrationService);
  #bluetoothData: BluetoothResponse;
  #isLoading = true;

  public constructor() {
    const iconRegistry = inject(MatIconRegistry);
    const sanitizer = inject(DomSanitizer);

    this.#translate.onLangChange.subscribe({
      error: err => this.#logger.error('Translation error', err),
    });

    // Add some custom sprites icons
    iconRegistry.addSvgIconSet(sanitizer.bypassSecurityTrustResourceUrl('assets/sprite/sprite.svg'));
  }

  public get isLoading(): boolean {
    return this.#isLoading;
  }

  public set isLoading(val: boolean) {
    this.#isLoading = val;
    if (val) {
      this.appSpinner?.spinnerTimeout.subscribe(err => {
        if (err === '') {
          err = this.#translate.instant('app.operationfailed');
        }
        this.#snackBarService.error(err);
      });
    }
  }

  public logout(): void {
    this.#authService.logout();
  }

  public async ngOnInit() {
    if (this.isMobile) {
      // Mobile is offline-first: the splash must never be gated on auth/network/platform-init progress.
      // We bound the blocking awaits and swallow errors so a stalled token refresh or hung HTTP call
      // can't keep the user stuck on the splash when a persisted user is available.
      await this.runMobileBootstrap();
    } else {
      await this.initEvcPlatform();
      await this.#currentUserService.init(); // CurrentUserService rely on AuthService.
      await Promise.all([
        this.#tenantRegistrationService.init(),
        this.#featureToggleService.init(), // FeatureToggleService rely on CurrentUserService.
      ]);
    }

    this.#persistedOrganizationService.init(); // PersistedOrganizationService rely on CurrentUserService and OrganizationsService (which rely on CurrentUserService).
    this.#media.observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge]).subscribe({
      next: subs => {
        const overMobileBreakPoint =
          subs.breakpoints[Breakpoints.Medium] || subs.breakpoints[Breakpoints.Large] || subs.breakpoints[Breakpoints.XLarge];
        this.openedOnInit = !this.isMobile && overMobileBreakPoint;
        this.sidenavMode = !subs.breakpoints[Breakpoints.XSmall] ? 'side' : 'over';
        this.opened = this.openedOnInit;
      },
    });

    this.routerNavigation();

    if (this.isMobile) {
      this.#syncBackupService.init();
      StatusBar.hide();
      document.getElementsByTagName('body').item(0).className += ' mobile-body';
      try {
        const isAutoSyncEnabled = await this.runPromiseWithTimeout(
          firstValueFrom(this.#featureToggleService.hasFeature(FeatureToggleCode.AutoSync)),
          5_000,
          'hasFeature(AutoSync)',
        );
        if (isAutoSyncEnabled) {
          this.#autoSyncService.start();
        }
      } catch (err) {
        this.#logger.error('Mobile init: autoSync check failed', err);
      }
    }

    this.isLoading = false;
  }

  public ngAfterViewInit(): void {
    document.body.style.minWidth = 'unset';
    this.checkDisconnectionIcon();
  }

  public checkDisconnectionIcon() {
    if (this.isMobile) {
      this.#bluetoothData = this.#bluetoothService.getBluetoothResponse();

      if (this.#bluetoothData) {
        this.#bluetoothService.setBluetoothConnectionState(true);
      } else {
        this.#bluetoothService.setBluetoothConnectionState(false);
      }
    }
  }

  public routerNavigation(): void {
    this.#router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        if (!(event.url === '/inspection-requests' || event.url.startsWith('/viewer') || event.url === '/isp-requests')) {
          // TODO: after finish modularization it won't be needed anymore
          this.isMobile = this.#routingService.getCurrentUrl().startsWith('/mobile');
        }
        if (event.url === '/mobile/no-data') {
          this.isLoading = false;
        }
      }
      if (event instanceof NavigationError) {
        this.#logger.error('Navigation Error', event.error);
      }
    });
  }

  public changeOrganization = (organizationId: Guid): Observable<void> =>
    this.#evcAuthService.login({
      extraQueryParameters: { toid: organizationId },
      redirectUri: '/',
      redirectStartPage: '/inspection-requests',
    });

  private setEvcPlatformOptions(): void {
    // input for app identification
    this.appIdentification = {
      onTitleClick: () => this.#router.navigateByUrl('/inspection-requests'),
      version: this.#appVersion,
    };

    // Setting Menu Initialization
    this.settingsMenu = [
      {
        key: 'Settings',
        text: this.#translate.instant('app.settings'),
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.#router.navigateByUrl('/settings/alarms'),
        },
      },
      {
        key: 'Report Builder',
        text: this.#translate.instant('app.reportBuilder'),
        action: {
          type: BtnActionEnum.URL,
          callbackOrUrl: this.configurationService.getConfiguration().reportTemplateBuilderEndpointUrl,
        },
      },
    ];
  }

  private async initEvcPlatform() {
    if (!this.isMobile) {
      this.setEvcPlatformOptions(); // Options must be set before evc-platform initialization.
    }
    // Even if we init the services in the app.config, we still need to wait for the init to complete before loading the app.
    await this.#platformService.init();
  }

  private async runMobileBootstrap(): Promise<void> {
    try {
      await this.runPromiseWithTimeout(this.initEvcPlatform(), 10_000, 'initEvcPlatform');
    } catch (err) {
      this.#logger.error('Mobile init: initEvcPlatform failed', err);
    }
    try {
      await this.#currentUserService.init();
    } catch (err) {
      this.#logger.error('Mobile init: currentUserService.init failed', err);
    }
    try {
      await this.#featureToggleService.init();
    } catch (err) {
      this.#logger.error('Mobile init: featureToggleService.init failed', err);
    }
  }

  private async runPromiseWithTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T | null> {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<null>(resolve => {
      timeoutId = setTimeout(() => {
        this.#logger.error(`Run with promise: ${label} timed out after ${ms}ms`, null);
        resolve(null);
      }, ms);
    });
    try {
      return await Promise.race([promise, timeout]);
    } finally {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    }
  }
}
